/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable eqeqeq */
/* eslint-disable no-underscore-dangle */
import React, { useContext, useMemo, useState } from 'react';
import { List, DataColumn, LinkColumn } from 'components/lists';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Config } from 'configs';
import { DateWrapper } from 'components/formInputs/wrappers/dateWrapper';
import { renderTime, weekDays } from 'utils/dayTimeHelpers';
import { formatMillisToDisplayDateTimeString, unspecifiedDateToMillis } from 'utils/dateManagement';
import moment from 'moment';
import { DropDownList } from 'components/formInputs/dropDownList';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { FormButton } from 'components/buttons/formButton';
import { ConfirmationDialog, ConfirmationDialogTypes } from 'components/lists/confirmationDialog';
import { SchedulePeriodForm } from './schedulePeriodForm';

export const ProductScheduleForm = ({ product, itemData, restaurants, allowedRestaurants, onSaved, onDeleted }) => {
  const { state: auth } = useContext(AuthContext);
  const { addNew, replaceById, deleteById } = useContext(CompanyContext);
  const { alertController } = useContext(AlertContext);

  const [dayTimes, setDayTimes] = useState(itemData?.day_times || []);
  const [selectedDayTime, setSelectedDayTime] = useState(null);
  const [selectedDayTimeInd, setSelectedDayTimeInd] = useState(-1);
  const [dayTimeModalOpen, setDayTimeModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const parseTimeString = (current, original) => {
    if (original === '') {
      return null;
    }

    if (moment(original, 'YYYY-MM-DD HH:mm', true).isValid()) {
      return moment(original, 'YYYY-MM-DD HH:mm', true).toDate();
    }

    return current;
  };

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    restaurants: yup
      .array()
      .of(yup.string())
      .required('Please enter a restaurant.')
      .min(1, 'Please select at least one restaurant.')
      .default(itemData?.restaurants),
    start_sale_from: yup
      .date()
      .typeError('Please enter a valid date')
      .transform(parseTimeString)
      .nullable(true)
      .default(itemData?.start_sale_from ? formatMillisToDisplayDateTimeString(itemData?.start_sale_from) : ''),
    stop_sale_at: yup
      .date()
      .typeError('Please enter a valid date')
      .transform(parseTimeString)
      .nullable(true)
      .default(itemData?.stop_sale_at ? formatMillisToDisplayDateTimeString(itemData?.stop_sale_at) : ''),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  const onDelete = async () => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.PRODUCT_SCHEDULE,
        token: auth.token,
        id: itemData._id,
        resourceV2: 'AdminProductSchedule',
        companyIdForV2: auth.company?.company,
      },
      () => {
        onDeleted();
        alertController.show('Schedule for product deleted.');
      },
    );
  };

  const onSubmit = async (data) => {
    const submitData = {
      product: product._id,
      restaurants: data.restaurants,
      start_sale_from: data.start_sale_from ? unspecifiedDateToMillis(data.start_sale_from) : undefined,
      stop_sale_at: data.stop_sale_at ? unspecifiedDateToMillis(data.stop_sale_at) : undefined,
      day_times: dayTimes ? dayTimes.map((x) => {
        const formed = {
          from_hour: x.from_hour,
          from_minute: x.from_minute,
          to_hour: x.to_hour,
          to_minute: x.to_minute,
        };

        if (x.day) {
          formed.day = x.day;
        }

        return formed;
      }) : [],
    };

    setIsLoading(true);

    if (!data._id) {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.PRODUCT_SCHEDULE,
          data: submitData,
          token: auth.token,
          resourceV2: 'AdminProductSchedule',
          companyIdForV2: auth.company?.company,
        },
        (newId) => {
          setIsLoading(false);
          submitData._id = newId;
          onSaved(submitData);
          alertController.show('Schedule for product updated successfully.');
        },
        () => {
          setIsLoading(false);
        },
      );
    } else {
      await replaceById(
        {
          collectionName: Config.DATA.COLLECTIONS.PRODUCT_SCHEDULE,
          id: itemData._id,
          data: submitData,
          token: auth.token,
          resourceV2: 'AdminProductSchedule',
          companyIdForV2: auth.company?.company,
        },
        async () => {
          setIsLoading(false);
          submitData._id = data._id;
          onSaved(submitData);
          alertController.show('Schedule for product updated successfully.');
        },
        () => {
          setIsLoading(false);
        },
      );
    }
  };
  const onError = (data) => console.log(data);

  const deleteSelectedRestaurant = (restaurantId) => {
    const newRestaurants = selectedRestaurants.filter((r) => r !== restaurantId);

    setSelectedRestaurants(newRestaurants);
    setValue('restaurants', newRestaurants);
  };

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <div className="mt-2">
        {selectedRestaurants.map((restaurant) => {
          const restaurantData = restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurantData._id}>
              <SelectedItemButton
                name={restaurantData?.name}
                onClick={() => deleteSelectedRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const getRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-' }, ...(restaurants?.filter((x) => allowedRestaurants && allowedRestaurants.includes(x._id)) || [])];

    return allRestaurants
      ?.filter((r) => !selectedRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: restaurant.name,
        value: restaurant._id,
      }));
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
        <div className="shadow sm:rounded-md sm:overflow-hidden">
          <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
            <div className="grid grid-cols-6 gap-6">

              <div className="col-span-6 sm:col-span-3">
                <DropDownList
                  showLabel
                  id="restaurants"
                  name="restaurants"
                  label="Restaurants"
                  options={getRestaurantsDataSource()}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  onChange={(e) => {
                    setSelectedRestaurants((oldArray) => [...oldArray, e.target.value]);
                    setValue('restaurants', [...selectedRestaurants, e.target.value]);
                  }}
                />
                {errors?.restaurants && (
                <p className="mt-2 text-sm text-red-600" id="email-error">
                  {errors?.restaurants.message}
                </p>
                )}
                {renderSelectedRestaurants()}
              </div>

              <div className="hidden col-span-6 sm:col-span-3 sm:block" />

              <DateWrapper
                register={register}
                name="start_sale_from"
                title="Start sale from"
                error={errors?.start_sale_from}
                responsiveClassName="col-span-6 sm:col-span-3"
                isDateTime
              />

              <DateWrapper
                register={register}
                name="stop_sale_at"
                title="Stop sale at"
                error={errors?.stop_sale_at}
                responsiveClassName="col-span-6 sm:col-span-3"
                isDateTime
              />
            </div>

            <div className="mt-6">
              <div className="flex flex-row flex-wrap justify-between items-center">
                <div>
                  <h3 className="text-base font-medium text-gray-900">Periods</h3>
                  <p className="text-sm text-gray-500">Define more specific periods of sale</p>
                </div>
                <div className="py-3 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDayTime(null);
                      setDayTimeModalOpen(true);
                    }}
                    className="bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Add period
                  </button>
                </div>
              </div>
            </div>

            {dayTimes && dayTimes.length > 0 && (
            <div className="col-span-6">
              <div className="-mt-7">
                <List
                  source={dayTimes || []}
                  onRowClick={(item, ind) => {
                    setSelectedDayTime(item);
                    setSelectedDayTimeInd(ind);
                    setDayTimeModalOpen(true);
                  }}
                  keyField="usage_id"
                >
                  <DataColumn
                    name="Day"
                    dbColumnName="day"
                    render={(item) => {
                      return weekDays.find((x) => x.value === item.day)?.label || '-';
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 table-cell"
                  />
                  <DataColumn
                    name="From"
                    dbColumnName="from"
                    render={(item) => {
                      return renderTime(item.from_hour, item.from_minute);
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 table-cell"
                  />
                  <DataColumn
                    name="Until"
                    dbColumnName="until"
                    render={(item) => {
                      return renderTime(item.to_hour, item.to_minute);
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 table-cell"
                  />
                  <LinkColumn
                    title="Delete"
                    onClick={(item, ind) => {
                      setDayTimes((prev) => {
                        const newArray = [...prev];

                        newArray[ind] = null;

                        return newArray.filter((x) => !!x);
                      });
                    }}
                    className="relative py-3.5 pl-3 sm:pr-6"
                    dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                  />
                </List>
              </div>
            </div>
            )}

          </div>

          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-between items-start">
            <button
              type="button"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => {
                if (itemData._id) {
                  setIsDeleteDialogOpen(true);
                } else {
                  onDeleted();
                }
              }}
            >
              Delete
            </button>
            <FormButton text={itemData._id ? 'Save' : 'Add new'} isLoading={isLoading} />
          </div>
        </div>
      </form>
      <SchedulePeriodForm
        isOpen={dayTimeModalOpen}
        closeModal={() => setDayTimeModalOpen(false)}
        itemData={selectedDayTime}
        onSave={(val) => {
          setDayTimes((prev) => {
            const foundInd = selectedDayTimeInd; //  prev.findIndex((x) => x.temp_id === selectedDayTime.temp_id);

            if (foundInd >= 0) {
              const newArray = [...prev];

              newArray[foundInd] = val;
              return newArray;
            }

            return [...prev, val];
          });
          setDayTimeModalOpen(false);
        }}
      />
      <ConfirmationDialog
        type={ConfirmationDialogTypes.DELETE}
        title="Delete schedule"
        description="Are you sure you want to delete the schedule?"
        isOpen={isDeleteDialogOpen}
        closeModal={() => setIsDeleteDialogOpen(false)}
        onSubmit={onDelete}
      />
    </>
  );
};
